import * as React from 'react';

import { DEFAULT_LANGUAGE } from '@/inpatient-patient-pwa/i18n/constants';
import useI18nResolvers from '@/inpatient-patient-pwa/i18n/useI18nResolvers';

/** The slice of a survey page's `data` this hook reads. */
interface ImprovementsData {
  improvements?: Array<string>;
  improvementsI18N?: Record<string, Array<string>>;
}

export interface Improvements {
  /** Labels to render, in the language currently on screen. */
  titles: Array<string>;
  /** Turns the labels stored in the answer into the ones on screen. */
  toDisplayed: (stored: Array<string>) => Array<string>;
  /** Turns the labels picked on screen into the ones to store in the answer. */
  toStored: (displayed: Array<string>) => Array<string>;
}

/**
 * Improvement labels for a survey question, plus the mapping its answer needs.
 *
 * The answer always stores the pt-BR labels, whatever language the patient is
 * reading, so reports and dashboards stay comparable across patients. Entries of
 * a locale map are index-aligned, which is what makes that round trip possible —
 * a label is translated by position, not by lookup.
 *
 * A label with no counterpart is stored as picked rather than dropped. This
 * happens when the locale arrays have different lengths — they are authored by
 * hand in JSONB and nothing enforces that every language lists the same options
 * — and it means the answer can carry a label in the language on screen instead
 * of pt-BR. That is deliberate: it keeps the patient from being blocked or
 * silently losing a choice, and an off-language label is visible in the database
 * and can be corrected, which a discarded answer could not be.
 */
const useImprovements = (data?: ImprovementsData | null): Improvements => {
  const { resolveI18nList } = useI18nResolvers();

  return React.useMemo(() => {
    const legacy = data?.improvements ?? [];
    const stored = data?.improvementsI18N?.[DEFAULT_LANGUAGE] ?? legacy;
    const titles = resolveI18nList(data?.improvementsI18N) ?? legacy;

    const translate =
      (from: Array<string>, to: Array<string>) => (labels: Array<string>) =>
        labels.map((label) => {
          const index = from.indexOf(label);
          return index === -1 ? label : to[index] ?? label;
        });

    return {
      titles,
      toDisplayed: translate(stored, titles),
      toStored: translate(titles, stored),
    };
  }, [data, resolveI18nList]);
};

export default useImprovements;
